import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from 'views/components';
import {Translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import formatters from '../shared/formatters';

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  color: '#A8A49E',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #EAEAEA',
};

const bodyCellSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F2F0EC',
};

const FORECAST_COLUMNS = [
  {
    key: 'week',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.Week',
    align: 'left',
    getValue: (week, weekKey) => formatters.formatWeekOfDate(weekKey),
  },
  {
    key: 'clients',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ScheduledClients',
    align: 'right',
    getValue: (week) => formatters.formatCount(week?.activeClientCount),
  },
  {
    key: 'caregivers',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.Caregivers',
    align: 'right',
    getValue: (week) => formatters.formatCount(week?.caregiverCount),
  },
  {
    key: 'scheduledHours',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ScheduledHours',
    align: 'right',
    getValue: (week) => formatters.formatHours(week?.scheduledHours),
  },
  {
    key: 'weightedHours',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.WeightedHours',
    align: 'right',
    getValue: (week) => formatters.formatHours(week?.weightedHours),
  },
  {
    key: 'revenue',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.Revenue',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.revenue),
  },
  {
    key: 'labor',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.Labor',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.laborCost),
  },
  {
    key: 'grossProfit',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.GrossProfit',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.grossProfit),
  },
  {
    key: 'grossMargin',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.GrossMargin',
    align: 'right',
    getValue: (week) => formatters.formatMargin(week?.grossMargin),
  },
];

const ACTUAL_COLUMNS = [
  {
    key: 'week',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.Week',
    align: 'left',
    getValue: (week, weekKey) => formatters.formatWeekOfDate(weekKey),
  },
  {
    key: 'scheduledHours',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ScheduledHours',
    align: 'right',
    getValue: (week) => formatters.formatHours(week?.totalHours),
  },
  {
    key: 'confirmedHours',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ConfirmedHours',
    align: 'right',
    getValue: (week) => formatters.formatHours(week?.confirmedHours),
  },
  {
    key: 'scheduledRevenue',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ScheduledRevenue',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.scheduledRevenue),
  },
  {
    key: 'actualRevenue',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ActualRevenue',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.actualRevenue),
  },
  {
    key: 'actualLabor',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ActualLabor',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.actualLaborCost),
  },
  {
    key: 'grossProfit',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.GrossProfit',
    align: 'right',
    getValue: (week) => formatters.formatCurrency(week?.grossProfit),
  },
  {
    key: 'grossMargin',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.GrossMargin',
    align: 'right',
    getValue: (week) => formatters.formatMargin(week?.grossMargin),
  },
  {
    key: 'clientSlips',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.ClientSlips',
    align: 'right',
    getValue: (week) => formatters.formatSlipCompletion(week?.completeClientSlipCount, week?.incompleteClientSlipCount),
  },
  {
    key: 'caregiverSlips',
    headerExpression: 'metrics.ClientMetrics.WeeklyBreakdown.CaregiverSlips',
    align: 'right',
    getValue: (week) => formatters.formatSlipCompletion(week?.completeCaregiverSlipCount, week?.incompleteCaregiverSlipCount),
  },
];

const WeeklyBreakdownSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const forecastWeekKeys = React.useMemo(() => {
    return formatters.sortWeekKeysAscending(currentSnapshot?.forecast?.weeks);
  }, [currentSnapshot]);
  const actualWeekKeys = React.useMemo(() => {
    // Actuals read most-recent first.
    return formatters.sortWeekKeysAscending(currentSnapshot?.actual?.weeks).reverse();
  }, [currentSnapshot]);

  const renderTablePanel = (titleExpression, subtitleExpression, columns, weekKeys, weeks, emptyExpression) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        boxShadow: 'none',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        <Translate expression={titleExpression} />
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={subtitleExpression} />
      </Typography>
      {
        !weekKeys.length &&
        <Alert severity='info'>
          <Translate expression={emptyExpression} />
        </Alert>
      }
      {
        weekKeys.length > 0 &&
        <TableContainer sx={{overflowX: 'auto'}}>
          <Table size='small' sx={{minWidth: 760}}>
            <TableHead>
              <TableRow>
                {
                  columns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                      <Translate expression={column.headerExpression} />
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                weekKeys.map((weekKey) => (
                  <TableRow key={weekKey}>
                    {
                      columns.map((column) => (
                        <TableCell key={column.key} align={column.align} sx={bodyCellSx}>
                          {column.getValue(weeks?.[weekKey], weekKey)}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      }
    </Paper>
  );

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.WeeklyBreakdown.SectionTitle' />
      {
        isLoading &&
        <Stack spacing={2}>
          <PanelSkeleton height={280} />
          <PanelSkeleton height={280} />
        </Stack>
      }
      {
        !isLoading &&
        <Stack spacing={2}>
          {
            renderTablePanel(
                'metrics.ClientMetrics.WeeklyBreakdown.ForecastTitle',
                'metrics.ClientMetrics.WeeklyBreakdown.ForecastSubtitle',
                FORECAST_COLUMNS,
                forecastWeekKeys,
                currentSnapshot?.forecast?.weeks,
                'metrics.ClientMetrics.EmptyStates.NoForecastWeeks',
            )
          }
          {
            renderTablePanel(
                'metrics.ClientMetrics.WeeklyBreakdown.ActualTitle',
                'metrics.ClientMetrics.WeeklyBreakdown.ActualSubtitle',
                ACTUAL_COLUMNS,
                actualWeekKeys,
                currentSnapshot?.actual?.weeks,
                'metrics.ClientMetrics.EmptyStates.NoActualWeeks',
            )
          }
        </Stack>
      }
    </Box>
  );
};

WeeklyBreakdownSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(WeeklyBreakdownSection);
